/**
 * Conversion tracking helper.
 *
 * All conversions are pushed to the GTM dataLayer only. Google Tag Manager
 * (container `GTM-N76NSXP9`) is responsible for routing these events to any
 * downstream destination (GA4, Google Ads, Meta CAPI, etc). No Google Ads
 * (AW-*) or GA4 (G-*) tag IDs are hardcoded in the app.
 */

import { pushPurchaseEvent } from "@/lib/gtm";

export type ConversionEvent =
  | "begin_trial"
  | "purchase"
  | "generate_lead"
  | "begin_checkout";

interface ConversionPayload {
  value?: number;
  currency?: string;
  transaction_id?: string;
  label?: string;
}

/**
 * Push a conversion event to the GTM dataLayer. GTM handles routing.
 */
export function trackAdsConversion(
  event: ConversionEvent,
  payload: ConversionPayload = {}
): void {
  if (typeof window === "undefined") return;
  const { value, currency = "EUR", transaction_id, label } = payload;
  try {
    const w = window as unknown as { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer || [];
    const payloadOut: Record<string, unknown> = { event };
    if (value !== undefined) payloadOut.value = value;
    if (currency) payloadOut.currency = currency;
    if (transaction_id) payloadOut.transaction_id = transaction_id;
    if (label) payloadOut.event_label = label;
    w.dataLayer.push(payloadOut);
  } catch {
    /* noop */
  }
}

export const trackTrialStarted = (label?: string) =>
  trackAdsConversion("begin_trial", { value: 9, currency: "EUR", label });

/** Google Ads conversion IDs/labels (tag AW-18476134455). */
const GOOGLE_ADS_ID = "AW-18476134455";
const GOOGLE_ADS_PURCHASE_LABEL = "x_KjCPW1sfkcEPyqmJVE";

/** Fire the Google Ads purchase conversion directly on the gtag tag. */
function fireGoogleAdsPurchase(params: {
  value?: number;
  currency?: string;
  transactionId?: string;
}): void {
  if (typeof window === "undefined") return;
  try {
    const w = window as unknown as { gtag?: (...args: unknown[]) => void };
    if (typeof w.gtag !== "function") return;
    w.gtag("event", "conversion", {
      send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_PURCHASE_LABEL}`,
      value: params.value,
      currency: params.currency || "EUR",
      transaction_id: params.transactionId || "",
    });
  } catch {
    /* noop */
  }
}

export const trackPurchase = (params: {
  value: number;
  currency?: string;
  transactionId?: string;
  label?: string;
  contentName?: string;
  contentIds?: Array<string | number>;
  userEmail?: string;
  userPhone?: string;
  externalId?: string;
  status?: string;
}) => {
  trackAdsConversion("purchase", {
    value: params.value,
    currency: params.currency || "EUR",
    transaction_id: params.transactionId,
    label: params.label,
  });
  pushPurchaseEvent({
    value: params.value,
    currency: params.currency || "EUR",
    contentName: params.contentName,
    contentIds: params.contentIds,
    status: params.status,
    userEmail: params.userEmail,
    userPhone: params.userPhone,
    externalId: params.externalId ?? params.transactionId,
  });
  fireGoogleAdsPurchase({
    value: params.value,
    currency: params.currency || "EUR",
    transactionId: params.transactionId,
  });
};

export const trackLead = (label?: string) =>
  trackAdsConversion("generate_lead", { value: 1, currency: "EUR", label });

export const trackBeginCheckout = (value?: number, label?: string) =>
  trackAdsConversion("begin_checkout", { value, currency: "EUR", label });